'use client';

import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import PickupLocationListItem, { type SuggestionType } from '@molecules/PickupLocationListItem/PickupLocationListItem';
import Modal from '@organisms/Modal/Modal';
import Heading from '@atoms/Heading/Heading';
import Text from '@atoms/Text/Text';
import { useTranslations } from 'next-intl';
import { trackMapClick, trackStoreFinderSearchField } from '@helpers/analyticsHelpers/trackStoreFinder';
import KEYS from '@helpers/keys';
import {
  StyledDropdown,
  StyledContentWrapper,
  StyledStoreSearchEmptyState,
  StyledInputField,
  StyledMap,
  StyledModalButton,
  StyledSearchContainer,
  StyledSpinner,
  StyledStoreFinderForm,
} from './StoreFinder.styles';
import Takeover from '@molecules/Takeover/Takeover';
import OutsideClickWatcher from '@utility/OutsideClickWatcher/OutsideClickWatcher';
import useResponsive from '@hooks/useResponsive';
import useScrollLock from '@hooks/useScrollLock';
import { GeoPoint } from '@occ/api-client';
import useConfig from '@hooks/useConfig';
import dynamic from 'next/dynamic';
import Location from '@icons/location.svg';
import useListboxNavigation from '@hooks/useListboxNavigation';
import Listbox from '@molecules/Listbox/Listbox';
import Button from '@atoms/Button/Button';
import useStoreSearch from './useStoreSearch';
import useFindNearestStore from './useFindNearestStore';

const DynamicMap = dynamic(
  () => import('@molecules/StoreFinderMap/StoreFinderMap' /* webpackChunkName 'StoreFinderMap' */)
);

// Stockholm
const defaultLocation: GeoPoint = {
  latitude: 59.32903445070856,
  longitude: 18.061227888571498,
};

const StoreFinder = () => {
  const t = useTranslations('storefinder');
  const tModal = useTranslations('modal');
  const inputRef = useRef<HTMLInputElement>(null);
  const autoCompleteRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive();
  const { config } = useConfig();

  const [selectedStore, setSelectedStore] = useState<SuggestionType | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<GeoPoint>(defaultLocation);
  const [zoom, setZoom] = useState<number>(5);
  const [takeoverVisible, setTakeoverVisible] = useState(false);

  const {
    inputValue,
    setInputValue,
    suggestions,
    shouldShowSuggestions,
    setShouldShowSuggestions,
    onInputChange,
    onClear,
    selectStore,
    isLoading,
  } = useStoreSearch();

  const { allStores, isFindingNearest, showGeolocationErrorModal, setShowGeolocationErrorModal, findNearestStore } =
    useFindNearestStore();

  const onItemClick = (store: SuggestionType) => {
    setTakeoverVisible(false);
    setZoom(14);
    setSelectedStore(store);
    selectStore(store);
  };

  const { getListItemProps } = useListboxNavigation({
    inputRef,
    listRef: autoCompleteRef,
    onSelect: (index) => {
      const store = suggestions[index];
      if (store) {
        onItemClick(store);
      }
    },
  });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYS.UP:
        e.preventDefault();
        setShouldShowSuggestions(false);
        break;
      case KEYS.DOWN:
        e.preventDefault();
        setShouldShowSuggestions(true);
        autoCompleteRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
        break;
      default:
        break;
    }
  };

  const onBlurHandler = () => {
    setShouldShowSuggestions(false);
    setTakeoverVisible(false);
  };

  const handleFindStore = () => {
    findNearestStore((store) => {
      setZoom(14);
      setSelectedStore(store);
      setInputValue(store.name || '');
    });
  };

  useEffect(() => {
    if (selectedStore) setSelectedLocation(selectedStore.geoPoint || defaultLocation);
  }, [selectedStore]);

  // Close takeover when viewport switches to desktop
  // Open takeover when viewport switches to mobile with suggestions showing
  useEffect(() => {
    if (!isMobile && takeoverVisible) {
      setTakeoverVisible(false);
    }
    if (isMobile && shouldShowSuggestions && !takeoverVisible) {
      setTakeoverVisible(true);
    }
  }, [isMobile]);

  useScrollLock(takeoverVisible);

  return (
    <>
      <StyledStoreFinderForm>
        <StyledSearchContainer>
          <Takeover isVisible={takeoverVisible} onClose={onBlurHandler}>
            <OutsideClickWatcher clickHandler={() => setShouldShowSuggestions(false)}>
              <StyledInputField
                ref={inputRef}
                name="find-store"
                type="search"
                showSearchIcon
                aria-label={t('search.placeholder')}
                value={inputValue}
                placeholder={t('search.placeholder')}
                onChange={onInputChange}
                onFocus={() => {
                  isMobile && setTakeoverVisible(true);
                  if (suggestions.length > 0 || inputValue.length > 2) setShouldShowSuggestions(true);
                  trackStoreFinderSearchField();
                }}
                $open={shouldShowSuggestions || isLoading}
                $takeover={takeoverVisible}
                onKeyDown={onKeyDown}
                autoFocus={takeoverVisible}
                clearable
                onClear={onClear}
              />
              {(shouldShowSuggestions || isLoading) && (
                <StyledDropdown $isTakeover={takeoverVisible}>
                  <Listbox
                    listRef={autoCompleteRef}
                    items={suggestions}
                    getListItemProps={getListItemProps}
                    cards={isMobile}
                    emptyState={!isLoading && <StyledStoreSearchEmptyState searchTerm={inputValue} />}
                  >
                    {(item: SuggestionType) => (
                      <PickupLocationListItem
                        suggestion={item}
                        searchTerm={inputValue}
                        onItemClick={onItemClick}
                        isOrderReopened={false}
                        selectedStore={selectedStore?.storeId || '2110'}
                      />
                    )}
                  </Listbox>
                  {isLoading && <StyledSpinner size={32} color="black" />}
                </StyledDropdown>
              )}
            </OutsideClickWatcher>
          </Takeover>
        </StyledSearchContainer>
        <Button
          variant="primary"
          onClick={handleFindStore}
          isSubmitting={isFindingNearest}
          disabled={!allStores || allStores?.length <= 0}
          icon={Location}
          fullWidth={isMobile}
        >
          {t('findNearestStore')}
        </Button>
      </StyledStoreFinderForm>

      <StyledMap>
        <DynamicMap
          position={selectedLocation}
          stores={allStores || []}
          zoom={zoom}
          showMarkers={!!selectedStore}
          // TODO: Remove cast when @occ/api-client includes displayName in AxfoodStoreInfoViewModel
          selectedStore={selectedStore as any}
          onMarkerClick={(store) => {
            trackMapClick(store);
          }}
          googleMapsApiKey={config?.GOOGLE_MAPS_API_KEY || ''}
        />
      </StyledMap>

      {showGeolocationErrorModal && (
        <Modal size="small" onClose={() => setShowGeolocationErrorModal(false)}>
          <StyledContentWrapper>
            <Heading variant="h3">{t('geoLocationDisabled.title')}</Heading>
            <Text type="body" size="small">
              {t('geoLocationDisabled.body')}
            </Text>
            <StyledModalButton onClick={() => setShowGeolocationErrorModal(false)}>
              {tModal('modal.close')}
            </StyledModalButton>
          </StyledContentWrapper>
        </Modal>
      )}
    </>
  );
};

export default StoreFinder;
